import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Layers3, Activity, Clock3, Sigma } from "lucide-react";
import { useTranslation } from "react-i18next";

function TotalWithIcon({ value, digits = 3 }) {
  const num = Number(value);
  const formatted = formatNumber(value, digits);
  if (formatted === "—") return <span>{formatted}</span>;

  if (num < 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-blue-600">
        <img
          src="/assets/capacitor.png"
          alt="capacitor"
          style={{ width: 18, height: 18, objectFit: "contain" }}
        />
        <span>{formatted}</span>
      </span>
    );
  }
  if (num > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-orange-600">
        <img
          src="/assets/coil.png"
          alt="coil"
          style={{ width: 18, height: 18, objectFit: "contain" }}
        />
        <span>{formatted}</span>
      </span>
    );
  }
  return <span className="text-slate-800">{formatted}</span>;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function formatNumber(value, digits = 3) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toFixed(digits);
}

function formatDuration(seconds, lang = "en") {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return "—";
  const total = Math.max(0, Math.floor(Number(seconds)));

  const monthSec = 30 * 24 * 60 * 60;
  const daySec = 24 * 60 * 60;
  const hourSec = 60 * 60;
  const minSec = 60;

  let rem = total;
  const months = Math.floor(rem / monthSec);
  rem %= monthSec;
  const days = Math.floor(rem / daySec);
  rem %= daySec;
  const hours = Math.floor(rem / hourSec);
  rem %= hourSec;
  const mins = Math.floor(rem / minSec);
  const secs = rem % minSec;

  const isTr = String(lang || "").toLowerCase().startsWith("tr");
  const labels = isTr
    ? { mo: "ay", d: "gün", h: "sa", m: "dk", s: "sn" }
    : { mo: "mo", d: "d", h: "h", m: "m", s: "s" };

  const parts = [];
  if (months > 0) parts.push(`${months} ${labels.mo}`);
  if (days > 0) parts.push(`${days} ${labels.d}`);
  if (hours > 0) parts.push(`${hours} ${labels.h}`);
  if (mins > 0) parts.push(`${mins} ${labels.m}`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs} ${labels.s}`);

  return parts.slice(0, 3).join(" ");
}

export default function ReactiveStepsPage() {
  const { t, i18n } = useTranslation();
  const { deviceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ steps: [], statusWord: null, lastUpdate: null });
  const visibleSteps = useMemo(
    () => (data.steps || []).filter((s) => Number(s.step) <= 15),
    [data.steps]
  );

  useEffect(() => {
    if (!deviceId) return;

    let isCancelled = false;
    const token = localStorage.getItem("token");

    const fetchSteps = async () => {
      try {
        const res = await fetch(`${API_BASE}/reactive/steps?deviceId=${deviceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Request failed");
        const json = await res.json();
        if (!isCancelled) {
          setData(json || { steps: [] });
          setError("");
        }
      } catch (e) {
        if (!isCancelled) {
          setError(t("reactiveSteps.fetchError"));
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchSteps();
    const i = setInterval(fetchSteps, 10000);
    return () => {
      isCancelled = true;
      clearInterval(i);
    };
  }, [deviceId, t]);

  const summary = useMemo(() => {
    const steps = visibleSteps;
    const activeCount = steps.filter((s) => s.isOn === true).length;
    const totals = steps.reduce(
      (acc, s) => {
        acc.L1 += Number(s?.power?.L1) || 0;
        acc.L2 += Number(s?.power?.L2) || 0;
        acc.L3 += Number(s?.power?.L3) || 0;
        return acc;
      },
      { L1: 0, L2: 0, L3: 0 }
    );
    return {
      activeCount,
      totalCount: steps.length,
      ...totals
    };
  }, [visibleSteps]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-10">
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-xl bg-cyan-100 p-2">
            <Layers3 className="h-6 w-6 text-cyan-700" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">{t("reactiveSteps.title")}</h1>
            {data.lastUpdate && (
              <p className="text-sm text-slate-500">
                {t("reactiveSteps.lastUpdate")}: {new Date(data.lastUpdate).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-slate-600">
              <Activity className="h-4 w-4" />
              <span className="text-sm">{t("reactiveSteps.activeSteps")}</span>
            </div>
            <p className="text-2xl font-semibold text-slate-800">
              {summary.activeCount} / {summary.totalCount}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-slate-600">
              <Sigma className="h-4 w-4" />
              <span className="text-sm">{t("reactiveSteps.totalL1")}</span>
            </div>
            <p className="text-2xl font-semibold text-slate-800">{formatNumber(summary.L1)} kVar</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-slate-600">
              <Sigma className="h-4 w-4" />
              <span className="text-sm">{t("reactiveSteps.totalL2")}</span>
            </div>
            <p className="text-2xl font-semibold text-slate-800">{formatNumber(summary.L2)} kVar</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-slate-600">
              <Sigma className="h-4 w-4" />
              <span className="text-sm">{t("reactiveSteps.totalL3")}</span>
            </div>
            <p className="text-2xl font-semibold text-slate-800">{formatNumber(summary.L3)} kVar</p>
          </div>
        </div>

        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
            {t("reactiveSteps.loading")}
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700 shadow-sm">
            {error}
          </div>
        ) : (
          <>
            <div className="space-y-3 md:hidden">
              {visibleSteps.map((s) => {
                const on = s.isOn === true;
                const unknown = s.isOn === null || s.isOn === undefined;
                const relayText =
                  s.relayStatus === null || s.relayStatus === undefined ? "—" : String(s.relayStatus);
                return (
                  <div key={s.step} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="font-semibold text-slate-800">{t("reactiveSteps.stepN", { n: s.step })}</p>
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${unknown
                          ? "bg-amber-100 text-amber-700"
                          : on
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-200 text-slate-600"
                          }`}
                      >
                        {unknown ? t("reactiveSteps.unknown") : on ? t("reactiveSteps.on") : t("reactiveSteps.off")}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <p className="text-slate-500">L1</p>
                      <p className="text-right text-slate-700">{formatNumber(s?.power?.L1)} kVar</p>
                      <p className="text-slate-500">L2</p>
                      <p className="text-right text-slate-700">{formatNumber(s?.power?.L2)} kVar</p>
                      <p className="text-slate-500">L3</p>
                      <p className="text-right text-slate-700">{formatNumber(s?.power?.L3)} kVar</p>
                      <p className="text-slate-500">{t("reactiveSteps.total")}</p>
                      <p className="text-right font-medium"><TotalWithIcon value={s?.power?.total} /> kVar</p>
                      <p className="text-slate-500">{t("reactiveSteps.relay")}</p>
                      <p className="text-right text-slate-700">{relayText}</p>
                      <p className="text-slate-500">{t("reactiveSteps.activeTime")}</p>
                      <p className="text-right text-slate-700">
                        {s.activeTimeSec === null || s.activeTimeSec === undefined
                          ? "—"
                          : formatDuration(s.activeTimeSec, i18n.language)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm md:block">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left">{t("reactiveSteps.step")}</th>
                    <th className="px-4 py-3 text-right">L1 (kVar)</th>
                    <th className="px-4 py-3 text-right">L2 (kVar)</th>
                    <th className="px-4 py-3 text-right">L3 (kVar)</th>
                    <th className="px-4 py-3 text-right">{t("reactiveSteps.total")}</th>
                    <th className="px-4 py-3 text-center">{t("reactiveSteps.relay")}</th>
                    <th className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1">
                        <Clock3 className="h-4 w-4" />
                        {t("reactiveSteps.activeTime")}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-center">{t("reactiveSteps.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSteps.map((s) => {
                    const on = s.isOn === true;
                    const unknown = s.isOn === null || s.isOn === undefined;
                    const relayText =
                      s.relayStatus === null || s.relayStatus === undefined ? "—" : String(s.relayStatus);
                    return (
                      <tr key={s.step} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800">{t("reactiveSteps.stepN", { n: s.step })}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{formatNumber(s?.power?.L1)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{formatNumber(s?.power?.L2)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{formatNumber(s?.power?.L3)}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          <TotalWithIcon value={s?.power?.total} />
                        </td>
                        <td className="px-4 py-3 text-center text-slate-700">{relayText}</td>
                        <td className="px-4 py-3 text-center text-slate-700">
                          {s.activeTimeSec === null || s.activeTimeSec === undefined
                            ? "—"
                            : (
                              <div className="leading-tight">
                                <div>{formatDuration(s.activeTimeSec, i18n.language)}</div>
                                <div className="text-xs text-slate-400">
                                  {Math.round(Number(s.activeTimeSec))} s
                                </div>
                              </div>
                            )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${unknown
                              ? "bg-amber-100 text-amber-700"
                              : on
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-200 text-slate-600"
                              }`}
                          >
                            {unknown ? t("reactiveSteps.unknown") : on ? t("reactiveSteps.on") : t("reactiveSteps.off")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
